"use client";

import {
	chakra,
	GridItem,
	Link,
	LinkBox,
	LinkOverlay,
	useMultiStyleConfig,
	VisuallyHidden,
} from "@chakra-ui/react";
import React from "react";
import { DatoAsset, DatoAssetData } from "src/components/Dato/DatoAsset";
import { useTranslatedString } from "src/i18n/hooks";
import { ArrowRight } from "src/icons";
import { HeadingLevelBoundary, Hx } from "../Heading/Heading";
import { NextLink } from "../NextLink";
import { FormattedPriceDescription } from "../Product/Product";
import {
	StructuredText,
	StructuredTextData,
} from "../StructuredText/StructuredText";

type ProductOverviewItemProps = {
	id: string;
	name: string;
	href: string;
	description: string | StructuredTextData;
	image: DatoAssetData | null;
	externalLink?: { label: string | null; href: string } | null;
	price: number | null;
	priceDescription: string | null;
	isOnlineApplication: boolean;
};

export const ProductOverviewItem: React.FC<ProductOverviewItemProps> = ({
	isOnlineApplication,
	description,
	...props
}) => {
	const t = useTranslatedString();

	const styles = useMultiStyleConfig("ProductOverview", {
		isOnlineApplication,
	});

	const label = props.externalLink?.label;
	const href = props.externalLink?.href;

	return (
		<LinkBox as={GridItem} sx={styles.gridItem}>
			{props.image && (
				<DatoAsset
					objectFit="cover"
					height="12.5rem"
					data={props.image}
				/>
			)}
			<chakra.span __css={styles.productType}>
				{isOnlineApplication
					? t("productOverview.localProduct")
					: t("productOverview.externalProduct")}
			</chakra.span>

			<chakra.div __css={styles.textWrapper}>
				<LinkOverlay
					href={href ?? props.href}
					id={`click_product__${props.id}`}
				>
					<HeadingLevelBoundary>
						<Hx size="h4">{props.name}</Hx>
					</HeadingLevelBoundary>
				</LinkOverlay>
				{typeof description === "string" ? (
					<chakra.span>{description}</chakra.span>
				) : (
					<StructuredText data={description} />
				)}

				<chakra.div __css={styles.bottomWrapper}>
					{href ? (
						<Link
							sx={styles.externalLink}
							href={href}
							id={`click_product__${props.id}`}
							isExternal
						>
							<chakra.span sx={styles.externalLinkText}>
								{label?.length ? label : props.name}
							</chakra.span>
							<ArrowRight sx={styles.arrow} />
						</Link>
					) : (
						<>
							{props.price && (
								<chakra.div __css={styles.price}>
									<FormattedPriceDescription
										price={props.price}
										priceDescription={
											props.priceDescription ?? undefined
										}
									/>
								</chakra.div>
							)}

							<NextLink href={`/${props.href}`} passHref>
								<Link
									id={`click_product__${props.id}`}
									sx={styles.link}
								>
									<VisuallyHidden>
										{props.name}
									</VisuallyHidden>
									<ArrowRight sx={styles.arrow} />
								</Link>
							</NextLink>
						</>
					)}
				</chakra.div>
			</chakra.div>
		</LinkBox>
	);
};

// 🔬 TBD
